import { createAsyncThunk, type AnyAction, type ThunkDispatch } from '@reduxjs/toolkit'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'

import { AppDispatchType, RootStateType } from 'store'
import { toastFailed, toastSuccess } from 'utils/toast'
import { callErrorMsg } from 'helpers/errorMsg'
import { log } from 'utils/helpers/dataDogLog'
import { push } from 'utils/helpers/router'
import { isJsonBlob } from 'features/Enterprise/Vendor/pages/VendorInvoice/utils/downloadBlob'
import { resolveInvoiceFileDownloadTypeId } from 'features/Enterprise/Vendor/pages/VendorInvoice/utils/invoiceDocumentDownload'
import {
  getVendorInvoicesAPI,
  getInvoiceDetailsAPI,
  getInvoiceActivitiesAPI,
  getPODetailsAPI,
  getStatusActiveInvoicesAPI,
  putChangeDueDateAPI,
  putInvoiceStatusToReviewedAPI,
  putRejectInvoiceAPI,
  putReviseInvoiceAPI,
  getReasonOptionsAPI,
  putInvoiceStatusToApprovedAPI,
  putMarkAsPaidAPI,
  getDocFileUrlAPI,
  getInvoicePoMatchingsAPI,
  saveInvoicePoMatchingsAPI,
  addManualInvoiceItemsAPI,
  deleteInvoicePoItemAPI,
  getInvoicePoMatchingDetailAPI,
  saveInvoicePoMatchingDetailAPI,
  getInvoicePoEditHistoryAPI,
  triggerInvoicePoSkuMatchingAPI,
  type ChangeDueDateDataInterface,
  type RejectInvoicePayloadType,
  type ReviseInvoicePayloadType,
  type SaveInvoicePoMatchingDetailPayloadType,
  type InvoicePoMatchingsResponseType,
  type InvoiceDocumentKindType,
  type InvoiceDocumentInterface,
  PODetailsInInvoiceType,
} from 'features/Enterprise/Vendor/services/vendorInvoice'
import {
  VendorInvoiceReviewRouter,
  VendorInvoiceListRouter,
} from 'features/Enterprise/Vendor/router'
import { resolveInvoicePurchaseOrderIdForApi } from 'features/Enterprise/Vendor/pages/VendorInvoice/utils/invoicePurchaseOrder'
import {
  pollInvoicePoSkuMatchingResult,
  resolveSkuMatchingTriggerRequestId,
  shouldRunSkuMatchingForMatchingsApi,
} from 'features/Enterprise/Vendor/pages/VendorInvoice/VendorInvoiceSkuMatch/skuMatchUtils'

import {
  SLICE_NAME,
  setLoading,
  setVendorInvoices,
  setInvoiceDetails,
  setPODetails,
  setStatusActiveInvoices,
  setInvoiceReasonOptions,
  setSkuMatchData,
  setSkuMatchFetchFailed,
  setMatchingDetailData,
  setMatchingDetailFetchFailed,
  setEditHistoryData,
  setInvoiceActivities,
  setActivitiesLoading,
} from './vendorInvoiceSlice'

const refetchInvoicePoMatchings = async (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  getState: () => unknown,
  invoiceId: number,
  invoicePurchaseOrderId: number,
) => {
  const { invoiceDetails, skuMatchData } = (getState() as RootStateType).vendorInvoice
  const purchaseOrder = invoiceDetails.purchase_orders?.find(
    (po) =>
      resolveInvoicePurchaseOrderIdForApi(po, skuMatchData[po.purchase_order_id]) ===
      invoicePurchaseOrderId,
  )
  if (!purchaseOrder) {
    return
  }
  await (dispatch as AppDispatchType)(
    fetchInvoicePoMatchings({ invoiceId, purchaseOrder, force: true }),
  ).unwrap()
}

const canRunAutoSkuMatching = (matchings: InvoicePoMatchingsResponseType | undefined) => {
  if (!matchings) {
    return false
  }
  return (
    matchings.invoice_items.length > 0 &&
    matchings.po_items.length > 0 &&
    shouldRunSkuMatchingForMatchingsApi(matchings)
  )
}

/**
 * `ai-be` (AI Gateway) is an optional dependency for auto SKU matching. When it fails,
 * the backend still responds HTTP 200 with `data: null` + `error` (code 13) or
 * `data.status = 'FAILED'`. A 200 must never block the user, so we only log it for
 * observability: no toast, and the panel stays out of its error state.
 */
const logSkuMatchingFallback = (reason: string, context: Record<string, unknown>) => {
  log.warn('SKU matching AI tidak tersedia, lanjut ke pencocokan manual', {
    customInfo: {
      data: { module: 'sku-matching', reason, ...context },
    },
  })
}

export const fetchVendorInvoices = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorInvoices`,
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true))
      const {
        vendorInvoice: { queryParams },
      } = getState() as RootStateType
      const { data } = await getVendorInvoicesAPI(queryParams)
      dispatch(setVendorInvoices(data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchActiveInvoices = createAsyncThunk(
  `${SLICE_NAME}/fetchActiveInvoices`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getStatusActiveInvoicesAPI()
      dispatch(setStatusActiveInvoices(data.data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchInvoiceDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoiceDetails`,
  async (invoiceId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getInvoiceDetailsAPI(invoiceId)
      dispatch(setInvoiceDetails(data.data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchInvoiceActivities = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoiceActivities`,
  async (invoiceId: string, { dispatch }) => {
    try {
      dispatch(setActivitiesLoading(true))
      const { data } = await getInvoiceActivitiesAPI(invoiceId)
      dispatch(setInvoiceActivities(data.data ?? []))
    } catch (error) {
      callErrorMsg(error, 'Gagal memuat aktivitas invoice')
      dispatch(setInvoiceActivities([]))
    } finally {
      dispatch(setActivitiesLoading(false))
    }
  },
)

export const fetchPODetails = createAsyncThunk(
  `${SLICE_NAME}/fetchPODetails`,
  async (poNumber: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getPODetailsAPI(poNumber)
      dispatch(setPODetails(data.data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchInvoicePoMatchings = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoicePoMatchings`,
  async (
    {
      invoiceId,
      purchaseOrder,
      force = false,
    }: {
      invoiceId: number
      purchaseOrder: PODetailsInInvoiceType
      force?: boolean
    },
    { dispatch, getState },
  ) => {
    const purchaseOrderId = purchaseOrder.purchase_order_id
    const {
      vendorInvoice: { skuMatchData },
    } = getState() as RootStateType

    if (!force && skuMatchData[purchaseOrderId]) {
      return
    }

    try {
      dispatch(
        setSkuMatchFetchFailed({
          purchaseOrderId,
          failed: false,
        }),
      )
      const invoicePurchaseOrderId = resolveInvoicePurchaseOrderIdForApi(
        purchaseOrder,
        skuMatchData[purchaseOrderId],
      )
      const { data } = await getInvoicePoMatchingsAPI(invoiceId, invoicePurchaseOrderId)
      dispatch(
        setSkuMatchData({
          purchaseOrderId: purchaseOrder.purchase_order_id,
          data: data.data,
        }),
      )
    } catch (error) {
      dispatch(
        setSkuMatchFetchFailed({
          purchaseOrderId: purchaseOrder.purchase_order_id,
          failed: true,
        }),
      )
      callErrorMsg(error, 'Gagal memuat data pencocokan SKU')
    }
  },
)

export const runAutoSkuMatchingForPo = createAsyncThunk(
  `${SLICE_NAME}/runAutoSkuMatchingForPo`,
  async (
    {
      invoiceId,
      purchaseOrder,
    }: {
      invoiceId: number
      purchaseOrder: PODetailsInInvoiceType
    },
    { dispatch, getState },
  ) => {
    const purchaseOrderId = purchaseOrder.purchase_order_id
    const {
      vendorInvoice: { skuMatchData },
    } = getState() as RootStateType

    const matchings = skuMatchData[purchaseOrderId]
    if (!canRunAutoSkuMatching(matchings)) {
      return { purchaseOrderId, matches: [] }
    }

    try {
      dispatch(
        setSkuMatchFetchFailed({
          purchaseOrderId,
          failed: false,
        }),
      )
      dispatch(setLoading(true))
      const invoicePurchaseOrderId = resolveInvoicePurchaseOrderIdForApi(purchaseOrder, matchings)
      const { data: triggerData } = await triggerInvoicePoSkuMatchingAPI(
        invoiceId,
        invoicePurchaseOrderId,
      )
      const requestId = resolveSkuMatchingTriggerRequestId(triggerData)

      if (requestId == null) {
        logSkuMatchingFallback('trigger-unavailable', {
          invoiceId,
          invoicePurchaseOrderId,
          errorCode: triggerData?.error?.code,
          errorMessage: triggerData?.error?.message,
        })
        return { purchaseOrderId, matches: [] }
      }

      const result = await pollInvoicePoSkuMatchingResult(
        invoiceId,
        invoicePurchaseOrderId,
        requestId,
      )

      if (result.status === 'FAILED') {
        logSkuMatchingFallback('result-failed', {
          invoiceId,
          invoicePurchaseOrderId,
          requestId,
          errorMessage: result.error_message,
        })
        return { purchaseOrderId, matches: [] }
      }

      if (result.status === 'SUCCESS' && result.matches.length > 0) {
        toastSuccess('Saran pencocokan AI diterapkan. Periksa lalu klik Selesai Cocokan.')
      }

      return { purchaseOrderId, matches: result.matches }
    } catch (error) {
      dispatch(
        setSkuMatchFetchFailed({
          purchaseOrderId: purchaseOrder.purchase_order_id,
          failed: true,
        }),
      )
      callErrorMsg(error, 'Gagal memulai pencocokan SKU otomatis')
      return { purchaseOrderId, matches: [] }
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const saveInvoicePoMatchings = createAsyncThunk(
  `${SLICE_NAME}/saveInvoicePoMatchings`,
  async (
    {
      invoiceId,
      invoicePurchaseOrderId,
      matchings,
    }: {
      invoiceId: number
      invoicePurchaseOrderId: number
      matchings: { invoice_item_id: number; po_item_ids: number[] }[]
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoading(true))
      await saveInvoicePoMatchingsAPI(invoiceId, invoicePurchaseOrderId, { matchings })
      await refetchInvoicePoMatchings(dispatch, getState, invoiceId, invoicePurchaseOrderId)
      toastSuccess('Pencocokan SKU berhasil disimpan')
    } catch (error) {
      callErrorMsg(error, 'Gagal menyimpan pencocokan SKU')
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const addManualInvoiceItems = createAsyncThunk(
  `${SLICE_NAME}/addManualInvoiceItems`,
  async (
    {
      invoiceId,
      invoicePurchaseOrderId,
      items,
    }: {
      invoiceId: number
      invoicePurchaseOrderId: number
      items: {
        vendor_sku_code: string
        vendor_sku_name: string
        invoice_unit_price: number
        invoice_quantity: number
        invoice_converter: number
        invoice_discount_percentage: number
        invoice_tax_setting: boolean
      }[]
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoading(true))
      await addManualInvoiceItemsAPI(invoiceId, invoicePurchaseOrderId, { items })
      await refetchInvoicePoMatchings(dispatch, getState, invoiceId, invoicePurchaseOrderId)
      toastSuccess('Item berhasil ditambahkan')
    } catch (error) {
      callErrorMsg(error, 'Gagal menyimpan SKU')
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const deleteInvoicePoItem = createAsyncThunk(
  `${SLICE_NAME}/deleteInvoicePoItem`,
  async (
    {
      invoiceId,
      purchaseOrder,
      invoicePoItemId,
    }: {
      invoiceId: number
      purchaseOrder: PODetailsInInvoiceType
      invoicePoItemId: number
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoading(true))
      const {
        vendorInvoice: { skuMatchData },
      } = getState() as RootStateType
      const invoicePurchaseOrderId = resolveInvoicePurchaseOrderIdForApi(
        purchaseOrder,
        skuMatchData[purchaseOrder.purchase_order_id],
      )
      await deleteInvoicePoItemAPI(invoiceId, invoicePurchaseOrderId, invoicePoItemId)
      await refetchInvoicePoMatchings(dispatch, getState, invoiceId, invoicePurchaseOrderId)
      toastSuccess('Item berhasil dihapus')
    } catch (error) {
      callErrorMsg(error, 'Gagal menghapus SKU')
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const deleteInvoicePoItems = createAsyncThunk(
  `${SLICE_NAME}/deleteInvoicePoItems`,
  async (
    {
      invoiceId,
      purchaseOrder,
      invoicePoItemIds,
    }: {
      invoiceId: number
      purchaseOrder: PODetailsInInvoiceType
      invoicePoItemIds: number[]
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoading(true))
      const {
        vendorInvoice: { skuMatchData },
      } = getState() as RootStateType
      const invoicePurchaseOrderId = resolveInvoicePurchaseOrderIdForApi(
        purchaseOrder,
        skuMatchData[purchaseOrder.purchase_order_id],
      )
      await Promise.all(
        invoicePoItemIds.map((invoicePoItemId) =>
          deleteInvoicePoItemAPI(invoiceId, invoicePurchaseOrderId, invoicePoItemId),
        ),
      )
      await refetchInvoicePoMatchings(dispatch, getState, invoiceId, invoicePurchaseOrderId)
      toastSuccess(`${invoicePoItemIds.length} item berhasil dihapus`)
    } catch (error) {
      callErrorMsg(error, 'Gagal menghapus SKU')
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchInvoicePoMatchingDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoicePoMatchingDetail`,
  async (
    { invoiceId, invoicePurchaseOrderId }: { invoiceId: number; invoicePurchaseOrderId: number },
    { dispatch },
  ) => {
    try {
      dispatch(setMatchingDetailFetchFailed({ invoicePurchaseOrderId, failed: false }))
      dispatch(setLoading(true))
      const { data } = await getInvoicePoMatchingDetailAPI(invoiceId, invoicePurchaseOrderId)
      dispatch(
        setMatchingDetailData({
          invoicePurchaseOrderId,
          data: data.data,
        }),
      )
    } catch (error) {
      dispatch(setMatchingDetailFetchFailed({ invoicePurchaseOrderId, failed: true }))
      callErrorMsg(error, 'Gagal memuat detail tagihan')
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const saveInvoicePoMatchingDetail = createAsyncThunk(
  `${SLICE_NAME}/saveInvoicePoMatchingDetail`,
  async (
    {
      invoiceId,
      invoicePurchaseOrderId,
      payload,
    }: {
      invoiceId: number
      invoicePurchaseOrderId: number
      payload: SaveInvoicePoMatchingDetailPayloadType
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await saveInvoicePoMatchingDetailAPI(invoiceId, invoicePurchaseOrderId, payload)
      await dispatch(fetchInvoicePoMatchingDetail({ invoiceId, invoicePurchaseOrderId }))
      await dispatch(fetchInvoiceDetails(String(invoiceId)))
      toastSuccess('Perubahan berhasil disimpan')
    } catch (error) {
      callErrorMsg(error, 'Gagal menyimpan perubahan tagihan')
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchInvoicePoEditHistory = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoicePoEditHistory`,
  async (
    { invoiceId, invoicePurchaseOrderId }: { invoiceId: number; invoicePurchaseOrderId: number },
    { dispatch },
  ) => {
    try {
      const { data } = await getInvoicePoEditHistoryAPI(invoiceId, invoicePurchaseOrderId)
      dispatch(
        setEditHistoryData({
          invoicePurchaseOrderId,
          entries: data.data.entries,
        }),
      )
    } catch (error) {
      callErrorMsg(error, 'Gagal memuat riwayat edit')
    }
  },
)

export const putInvoiceStatusToReviewed = createAsyncThunk(
  `${SLICE_NAME}/putInvoiceStatusToReviewed`,
  async (
    { invoiceId, isReviewPage }: { invoiceId: number; isReviewPage?: boolean },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putInvoiceStatusToReviewedAPI(invoiceId)

      if (isReviewPage) {
        dispatch(fetchInvoiceDetails(String(invoiceId)))
      } else {
        dispatch(push({ url: VendorInvoiceReviewRouter.path.replace(':id', String(invoiceId)) }))
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putInvoiceStatusToApproved = createAsyncThunk(
  `${SLICE_NAME}/putInvoiceStatusToApproved`,
  async (invoice_id: number, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await putInvoiceStatusToApprovedAPI(invoice_id)
      dispatch(push({ url: VendorInvoiceListRouter.path }))
      toastSuccess('Invoice berhasil disetujui')
    } catch (error) {
      const errorResponse = error as { response?: { data?: { error?: { message?: string } } } }
      const errorMsg = errorResponse?.response?.data?.error?.message || ''

      if (errorMsg.includes('unmatched') || errorMsg.includes('diff_unit_quantity')) {
        toastFailed(
          'Masih ada SKU yang belum dicocokkan atau qty berbeda. Selesaikan SKU match terlebih dahulu.',
        )
      } else if (errorMsg.includes('confirmation letter')) {
        toastFailed('Discrepancy >5% memerlukan surat konfirmasi')
      } else {
        callErrorMsg(error, 'Gagal menyetujui invoice')
      }
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putInvoiceStatusToPaid = createAsyncThunk(
  `${SLICE_NAME}/putInvoiceStatusToPaid`,
  async ({ invoiceId, file }: { invoiceId: number; file?: File }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      let formData: FormData | undefined
      if (file) {
        formData = new FormData()
        formData.append('payment_evidence', file)
      }
      await putMarkAsPaidAPI(invoiceId, formData)
      await dispatch(fetchInvoiceDetails(String(invoiceId)))
      toastSuccess('Invoice berhasil ditandai dibayar')
    } catch (error) {
      callErrorMsg(error, 'Gagal menandai invoice dibayar')
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putChangeDueDate = createAsyncThunk(
  `${SLICE_NAME}/putChangeDueDate`,
  async (
    {
      invoice_id,
      data,
      isReview,
      handleCloseDialog,
    }: {
      invoice_id: number
      isReview: boolean
      data: ChangeDueDateDataInterface
      handleCloseDialog: () => void
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putChangeDueDateAPI(invoice_id, data)

      if (isReview) {
        await dispatch(fetchInvoiceDetails(String(invoice_id)))
      } else {
        await dispatch(fetchVendorInvoices())
      }
      handleCloseDialog()
      toastSuccess('Berhasil mengubah jatuh tempo')
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchInvoiceReasonOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchInvoiceReasonOptions`,
  async (type: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getReasonOptionsAPI(type)
      dispatch(
        setInvoiceReasonOptions({
          type,
          reasons: data.data,
        }),
      )
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putRejectInvoice = createAsyncThunk(
  `${SLICE_NAME}/putRejectInvoice`,
  async (
    {
      invoice_id,
      payload,
      handleCloseDialog,
    }: {
      invoice_id: number
      payload: RejectInvoicePayloadType
      handleCloseDialog: () => void
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putRejectInvoiceAPI(invoice_id, payload)
      handleCloseDialog()
      toastSuccess('Invoice berhasil ditolak. Silahkan cek di riwayat invoice')
      dispatch(push({ url: VendorInvoiceListRouter.path }))
    } catch (error) {
      callErrorMsg(error, 'Gagal menolak invoice')
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putRevisionInvoice = createAsyncThunk(
  `${SLICE_NAME}/putRevisionInvoice`,
  async (
    {
      invoice_id,
      payload,
      handleCloseDialog,
      isReviewPage,
    }: {
      invoice_id: number
      payload: ReviseInvoicePayloadType
      handleCloseDialog: () => void
      isReviewPage?: boolean
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putReviseInvoiceAPI(invoice_id, payload)
      handleCloseDialog()
      toastSuccess('Invoice berhasil direvisi')

      if (isReviewPage) {
        await dispatch(fetchInvoiceDetails(String(invoice_id)))
      } else {
        dispatch(push({ url: VendorInvoiceListRouter.path }))
      }
    } catch (error) {
      callErrorMsg(error, 'Gagal meminta revisi invoice')
    } finally {
      dispatch(setLoading(false))
    }
  },
)

const mapFileType = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
}

export const fetchDocFileUrl = createAsyncThunk(
  `${SLICE_NAME}/fetchDocFileUrl`,
  async (
    {
      filePath,
      fileName,
      extension,
      documentType,
      failureMessage = 'Gagal mengunduh file',
    }: {
      filePath: string
      fileName: string
      extension: string
      documentType?: InvoiceDocumentKindType
      failureMessage?: string
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const downloadTypeId = resolveInvoiceFileDownloadTypeId(filePath, documentType)
      const { data } = await getDocFileUrlAPI(
        filePath,
        mapFileType[extension as keyof typeof mapFileType] || 'application/octet-stream',
        downloadTypeId,
      )

      if (await isJsonBlob(data)) {
        toastFailed(failureMessage)
        return
      }

      saveAs(data, fileName)
    } catch (error) {
      callErrorMsg(error, failureMessage)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchAllDocsFileUrl = createAsyncThunk(
  `${SLICE_NAME}/fetchAllDocsFileUrl`,
  async (
    {
      documents,
      zipName,
    }: {
      documents: Pick<InvoiceDocumentInterface, 'file_path' | 'document_type'>[]
      zipName: string
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const zip = new JSZip()

      const usedFilenames = new Set<string>()

      const filePromises = documents.map(async (document, idx) => {
        const url = document.file_path
        const originalName = url?.split('/')?.pop() || `file-${idx}`
        const extension = originalName?.split('.').pop() || 'ext'
        const baseName = originalName.replace(/\.[^/.]+$/, '')

        const type =
          mapFileType[extension as keyof typeof mapFileType] || 'application/octet-stream'
        const downloadTypeId = resolveInvoiceFileDownloadTypeId(url, document.document_type)

        let filename = `${baseName}.${extension}`
        let suffix = 1
        while (usedFilenames.has(filename)) {
          filename = `${baseName}-${suffix}.${extension}`
          suffix += 1
        }
        usedFilenames.add(filename)

        try {
          const { data } = await getDocFileUrlAPI(url, type, downloadTypeId)
          zip.file(filename, data)
          return { status: 'fulfilled', filename }
        } catch (error) {
          return { status: 'rejected', filename, error }
        }
      })

      const results = await Promise.all(filePromises)

      const failed = results.filter((result) => result.status === 'rejected')
      if (failed.length) {
        toastFailed(`${failed.length} file(s) failed to download`)
      }

      zip.generateAsync({ type: 'blob' }).then((blob) => {
        saveAs(blob, `${zipName}.zip`)
      })
    } catch (error) {
      callErrorMsg(error)
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)
